import { FC, useRef } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useBrowserQueryState } from '@telialt/common-hooks/src/useBrowserQueryState';
import { useDynamicQueryParamKey } from '@telialt/common-hooks/src/useDynamicQueryParamKey';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { useSearchParams } from '@telialt/utils/src/pathname';

import { SORTING_QUERY_PARAM, TELIA1_QUERY_PARAM } from '../../constants';
import { usePublicServiceOffersQuery } from '../../hooks/usePublicServiceOffersQuery';
import { PublicPlansConfiguration } from '../../model';
import {
    generateServiceOffersRequestParams,
    getCommitmentSelectOptions,
    getCommitmentValueByParamKey,
    getSortingValueByLabel,
} from '../../utils';
import { extractKnownFiltersFromQuery } from './components/Facets/transformers';
import { useFacetFilteringControls } from './components/Facets/useFacetFilteringControls';
import { PlansBlock } from './components/PlansBlock';
import { PlansListErrorNotification } from './components/PlansListErrorNotification';
import { PlansProductSchema } from './components/PlansProductSchema';

interface Props extends PublicPlansConfiguration {
    searchParams?: { [key: string]: string | undefined };
}

export const PublicWebPlans: FC<Props> = ({ offerGroup, segment, filters: hasFilters, sorting, searchParams }) => {
    const { t } = useTranslations();
    const query = useSearchParams();

    const commitmentKeys = getCommitmentSelectOptions(t, offerGroup).map((option) => option.paramKey);
    const [commitmentKey] = useDynamicQueryParamKey({ possibleKeys: commitmentKeys, searchParams });

    const [teliaOneKey] = useDynamicQueryParamKey({
        possibleKeys: [TELIA1_QUERY_PARAM],
        searchParams,
    });

    const initialSortLabel = searchParams?.[SORTING_QUERY_PARAM];
    const [sortLabel] = useBrowserQueryState<string>(SORTING_QUERY_PARAM, undefined, initialSortLabel);

    const params = generateServiceOffersRequestParams(
        {
            offerGroup,
            segment,
            channel: 'PublicWeb',
            sorting: getSortingValueByLabel({ label: sortLabel, t }),
            contractDuration: getCommitmentValueByParamKey({ paramKey: commitmentKey, t, offerGroup }),
            isTeliaOne: teliaOneKey,
        },
        extractKnownFiltersFromQuery(Object.fromEntries(query.entries()) || searchParams, t),
    );

    const { data, isFetching, isLoading, error } = usePublicServiceOffersQuery(params);
    useLogError(error, DDComponent.Plans);
    const { appliedFilters, facets, filters, onFilterChange, onRemoveAllFilters, onRemoveFilter } =
        useFacetFilteringControls({ facets: data?.facets || [], initialFilters: data?.filters || {} });

    const possibleCommitmentTypes = data?.possibleCommitmentTypes || [];
    const defaultCommitmentRef = useRef<string>(null);
    if (!commitmentKey && !defaultCommitmentRef.current) {
        // Set default commitment, based on data set in LupaSearch, can only be retrieved on fresh default load.
        defaultCommitmentRef.current = data?.filters?.contractDuration?.[0];
    }
    const defaultCommitmentKey = getCommitmentSelectOptions(t, offerGroup).find(
        ({ apiValue }) => apiValue === defaultCommitmentRef.current,
    )?.paramKey;

    if (error) {
        return <PlansListErrorNotification />;
    }

    return (
        <>
            <PlansBlock
                data={data}
                hasFilters={hasFilters}
                hasSorting={sorting}
                initialSortLabel={initialSortLabel}
                commitmentKey={commitmentKey}
                defaultCommitmentKey={defaultCommitmentKey}
                teliaOneKey={teliaOneKey}
                isLoading={isLoading}
                facets={facets}
                filters={filters}
                appliedFilters={appliedFilters}
                offerGroup={offerGroup}
                onFilterChange={onFilterChange}
                onRemoveFilter={onRemoveFilter}
                onRemoveAllFilters={onRemoveAllFilters}
                possibleCommitmentTypes={possibleCommitmentTypes}
                isFetching={isFetching}
            />
            <PlansProductSchema offerGroup={offerGroup} data={data} />
        </>
    );
};
